<template>
  <v-container class="pa-0 person-dialog-form-container">
    <v-row no-gutters>
      <v-col cols="12">
        <p v-if="errorMessage" id="error" class="error-message mt-2 text-start">
          {{ errorMessage }}
        </p>
        <ValidationObserver ref="observer">
          <v-form class="modal-form">
            <v-row class="ma-0">
              <v-col cols="6" class="pa-0 pr-2">
                <ValidationProvider
                  id="first_names"
                  v-slot="{ errors }"
                  name="first_names"
                  rules="required"
                >
                  <TextField
                    v-model.trim="person.firstNames"
                    :label="'First names*'"
                    :errors="errors"
                    :name="'first_names'"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="6" class="pa-0">
                <ValidationProvider
                  id="last_name"
                  v-slot="{ errors }"
                  name="last_name"
                  rules="required"
                >
                  <TextField
                    v-model.trim="person.lastName"
                    :label="'Last name*'"
                    :errors="errors"
                    :name="'last_name'"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-menu
              v-if="showDateOfBirth"
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template #activator="{ on }">
                <ValidationProvider
                  id="date_of_birth"
                  v-slot="{ errors }"
                  name="date_of_birth"
                  rules="required"
                  mode="lazy"
                >
                  <TextField
                    v-model="person.dateOfBirth"
                    :label="'Date of Birth*'"
                    :errors="errors"
                    :name="'date_of_birth'"
                    :readonly="true"
                    :isDateField="true"
                    @date-picker-click="on.click"
                  >
                    <template #prepend v-if="!whitelabel.omDesign">
                      <v-icon>{{ icons.mdiCalendar }}</v-icon>
                    </template>
                  </TextField>
                </ValidationProvider>
              </template>
              <v-date-picker
                ref="dateMenu"
                v-model="person.dateOfBirth"
                :max="currentDate"
                @input="dateMenu = false"
              />
            </v-menu>

            <v-row class="ma-0" v-if="showIsAdultCheckbox">
              <v-col cols="12" class="pa-0 pr-2 mb-5">
                <ValidationProvider
                  id="is_adult_confirmed"
                  v-slot="{ errors }"
                  name="is_adult_confirmed"
                  :rules="{ required: { allowFalse: false } }"
                >
                  <Checkbox
                    v-model="isAdultConfirmed"
                    :label="'Person is 18 years or older'"
                    :disabled="isAdultCheckboxDisabled"
                    :readonly="isAdultCheckboxDisabled"
                    :errors="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <div
              v-if="
                !showDateOfBirth || relation == 'LIVING_WILL_DECISION_MAKER'
              "
            >
              <ValidationProvider
                id="kinship"
                v-slot="{ errors }"
                name="kinship"
                :rules="isReferral ? '' : 'required'"
              >
                <ListSelect
                  v-model="person.kinship"
                  :items="kinshipDropdownOptions"
                  :label="
                    isReferral
                      ? 'This person\'s relationship to me'
                      : 'This person\'s relationship to me*'
                  "
                  :disabled="isChildOrSpouse"
                  :errors="errors"
                />
              </ValidationProvider>
            </div>

            <div
              v-if="
                person.kinship == 'CHILD' &&
                relation != 'INSURANCE_NOMINATED_BENEFICIARY'
              "
            >
              <ValidationProvider
                v-if="person.kinship == 'CHILD'"
                id="gender-child"
                v-slot="{ errors }"
                name="gender"
                rules="required"
              >
                <RadioSelect
                  v-model="person.gender"
                  :items="[
                    { text: 'Male', value: 'MALE' },
                    { text: 'Female', value: 'FEMALE' },
                  ]"
                  :errors="errors"
                  :group="'gender'"
                  :name="'gender'"
                />
              </ValidationProvider>
            </div>

            <div v-if="relation == 'INSURANCE_NOMINATED_BENEFICIARY'">
              <ValidationProvider
                v-if="person.kinship == 'CHILD'"
                id="gender"
                v-slot="{ errors }"
                name="gender"
                rules="required"
              >
                <label class="text-subtitle-1">Gender*</label>
                <RadioSelect
                  v-model="person.gender"
                  :items="[
                    { text: 'Male', value: 'MALE' },
                    { text: 'Female', value: 'FEMALE' },
                  ]"
                  :errors="errors"
                  :group="'gender'"
                  :name="'gender'"
                />
              </ValidationProvider>

              <label class="text-subtitle-1">Identity number details</label>
              <RadioSelect
                v-model="identityNumber.idType"
                :items="[
                  { text: 'National ID', value: 'ID' },
                  { text: 'Passport', value: 'PASSPORT' },
                  { text: 'None', value: 'n/a' },
                ]"
                :group="'identity'"
                :name="'identity'"
              />
              <ValidationProvider
                v-if="idTypeSelected"
                id="id_or_passport_number"
                v-slot="{ errors }"
                name="id_or_passport_number"
                :rules="`${
                  identityNumber.idType == 'ID'
                    ? 'required|id_number'
                    : 'required'
                }`"
              >
                <v-text-field
                  v-model.trim="identityNumber.number"
                  :label="idOrPassportNumberLabel"
                  :error-messages="errors != '' ? errors + '.' : ''"
                />
                <TextField
                  v-model.trim="identityNumber.number"
                  :label="idOrPassportNumberLabel"
                  :errors="errors"
                  :name="'id_or_passport_number'"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="identityNumber.idType == 'PASSPORT'"
                id="passport_country"
                v-slot="{ errors }"
                name="passport_country"
                rules="required"
                class="w-100"
              >
                <ListSelect
                  v-model="identityNumber.countryCode"
                  :items="$options.countryDropdownOptions"
                  :name="'passport_country'"
                  :label="'Passport country*'"
                  :errors="errors"
                />
              </ValidationProvider>
            </div>
            <div v-else-if="relationsWithIDs.includes(relation)">
              <v-row class="ma-0">
                <label class="text-subtitle-1"
                  >Identity number details(optional)</label
                >
                <RadioSelect
                  v-model="identityNumber.idType"
                  :items="[
                    { text: 'National ID', value: 'ID' },
                    { text: 'Passport', value: 'PASSPORT' },
                    { text: 'None', value: 'n/a' },
                  ]"
                  :group="'identity'"
                  :name="'identity'"
                />

                <ValidationProvider
                  v-if="identityNumber.idType !== 'n/a'"
                  id="passport_country"
                  v-slot="{ errors }"
                  name="passport_country"
                  rules="required"
                  class="w-100"
                >
                  <ListSelect
                    v-model="identityNumber.countryCode"
                    :items="$options.countryDropdownOptions"
                    :name="'passport_country'"
                    :label="idOrPassportCountryLabel"
                    :errors="errors"
                  />
                </ValidationProvider>

                <ValidationProvider
                  v-if="idTypeSelected"
                  id="id_or_passport_number"
                  v-slot="{ errors }"
                  name="id_or_passport_number"
                  :rules="`${idOrPassportValidationRules()}`"
                  class="w-100"
                >
                  <TextField
                    v-model.trim="identityNumber.number"
                    :label="idOrPassportNumberLabel"
                    :errors="errors"
                    :name="'id_or_passport_number'"
                  />
                </ValidationProvider>
              </v-row>
            </div>

            <ValidationProvider
              key="email"
              id="email"
              v-slot="{ errors }"
              name="email"
              :rules="
                decisionMaker || !!determineEmailRequired
                  ? conditionalRequirement
                  : 'email'
              "
            >
              <TextField
                v-model.trim="person.email"
                :label="
                  decisionMaker || !!determineEmailRequired
                    ? 'Email Address*'
                    : 'Email (optional)'
                "
                :errors="errors"
                :name="'email'"
              />
            </ValidationProvider>

            <ValidationProvider
              key="cell_number"
              id="cell_number"
              v-slot="{ errors }"
              name="cell_number"
              :rules="
                emergencyContacts || determineEmergencyContact
                  ? { phoneIntl: [person.phoneCountryCode], required: true }
                  : { phoneIntl: [person.phoneCountryCode] }
              "
            >
              <TextField
                v-model.trim="person.contactNumber"
                :label="
                  emergencyContacts || determineEmergencyContact
                    ? 'Cell number*'
                    : 'Cell number (optional)'
                "
                :errors="errors"
                :name="'cell_number'"
                :isCountryCode="true"
              >
                <template #prepend>
                  <vue-tel-input
                    ref="telInput1"
                    :preferred-countries="['ZA', 'GB']"
                    :default-country="phoneCountryDefault"
                    @country-changed="countrySelected"
                  >
                    <template #arrow-icon>
                      <strong>{{ countryDialCode }}</strong>
                    </template>
                  </vue-tel-input>
                </template>
              </TextField>
            </ValidationProvider>
            <ValidationProvider
              key="alternative_contact_number"
              v-if="emergencyContacts || determineEmergencyContact"
              id="alternative_contact_number"
              v-slot="{ errors }"
              name="alternative_contact_number"
              :rules="{ phoneIntl: [person.phoneCountryCode2] }"
            >
              <TextField
                v-model.trim="person.contactNumber2"
                :label="'Secondary number (optional)'"
                :errors="errors"
                :name="'alternative_contact_number'"
                :isCountryCode="true"
              >
                <template #prepend>
                  <vue-tel-input
                    ref="telInput2"
                    :preferred-countries="['ZA', 'GB']"
                    :default-country="phoneCountryDefault2"
                    @country-changed="countrySelected2"
                  >
                    <template #arrow-icon>
                      <strong>{{ countryDialCode2 }}</strong>
                    </template>
                  </vue-tel-input>
                </template>
              </TextField>
            </ValidationProvider>
          </v-form>
        </ValidationObserver>
      </v-col>

      <div class="crud-card-actions pb-0">
        <div
          v-if="isEditForm && !updateMissingDataBeforeCreateRelation"
          class="button-container"
        >
          <GradientButton
            :title="'Save'"
            :small="true"
            :callout="'right'"
            @gradientButtonClicked="save"
            :loading="buttonLoading"
          />
          <GradientButton
            v-if="showDelete"
            :title="`Remove ${toCapitalCase(personFirstName)}`"
            :small="true"
            :fill="false"
            @gradientButtonClicked="determineDeleteCheck"
          />
        </div>
        <div v-else class="button-container">
          <GradientButton
            :title="'Save And Continue'"
            :small="true"
            :type="'submit'"
            :callout="'right'"
            @gradientButtonClicked="save"
            :loading="buttonLoading"
          />
        </div>
      </div>
    </v-row>
  </v-container>
</template>

<script>
  import {
    getAge,
    kinshipOptions,
    kinshipOptionsWithChildAndSpouse,
  } from '../../constants/js/people.js';
  import {
    CREATE_PERSON_MUTATION,
    UPDATE_PERSON_MUTATION,
    CREATE_TESTATOR_RELATIONSHIP_MUTATION,
  } from '../../constants/graphql/people';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { countryDropdownOptions } from '../../constants/js/countries';
  import { parsePhoneNumber } from 'awesome-phonenumber';

  export default {
    name: 'PersonDialogForm',
    props: {
      personCreated: { type: Function },
      relation: { type: String, required: true },
      personToUpdate: { type: Object },
      personFirstName: { type: String },
      createNewPerson: { type: Boolean, default: true },
      updateMissingDataBeforeCreateRelation: { type: Boolean, default: false },
      testator: { type: Object, required: true },
      decisionMaker: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      emergencyContacts: {
        required: false,
        default: false,
        type: [String, Boolean],
      },
      showDelete: { type: Boolean, default: true, required: false },
      emailRequired: { type: Boolean, default: false, required: false },
    },
    mounted() {
      let event = '';
      switch (this.$props.relation) {
        case 'CHILD':
          event = 'your-people-add-new-child-popup';
          break;
        case 'CHILD_GUARDIAN':
          event = 'your-people-add-new-child-guardian-popup';
          break;
        case 'TRUSTEE':
          event = 'your-people-add-new-trustee-popup';
          break;
        case 'EXECUTOR':
          event = 'your-people-add-new-executor';
          break;
        case 'CO_EXECUTOR':
          event = 'your-people-add-new-co-executor';
          break;
        case 'ALTERNATIVE_EXECUTOR':
          event = 'your-people-add-new-alternative-executor-popup';
          break;
        case 'HEIR':
          event = 'your-people-add-new-heir';
          break;
        case 'REFERRAL':
          event = 'your-people-add-referral';
          break;
        default:
          break;
      }

      if (event !== '') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: event,
          omuserid: this.$store.getters?.userId,
        });
      }
    },
    data() {
      return {
        countryDialCode: '+27',
        countryDialCode2: '+27',
        person: {
          firstNames: null,
          lastName: null,
          dateOfBirth: null,
          kinship: null,
          email: null,
          contactNumber: null,
          phoneCountryCode: 'ZA',
          contactNumber2: null,
          phoneCountryCode2: 'ZA',
        },
        identityNumber: {
          idType: 'n/a',
        },
        dateMenu: false,
        buttonLoading: false,
        errorMessage: null,
        vueTelInputKey: 'initial-load',
        conditionalRequirement: 'email|required',
        decisionMakerDialog: false,
        isAdultConfirmed: null,
        isReferral: window.location.pathname.includes('referrals'),
      };
    },
    computed: {
      relationsWithIDs() {
        return [
          'EXECUTOR',
          'CO_EXECUTOR',
          'ALTERNATIVE_EXECUTOR',
          'CHILD_GUARDIAN',
          'HEIR',
          'SPOUSE',
          'TRUSTEE',
          'LIVING_WILL_DECISION_MAKER',
        ];
      },
      kinshipDropdownOptions() {
        return this.testator.willCountry == 'GB'
          ? this.kinshipDropdownOptionsGB
          : this.kinshipDropdownOptionsDefault;
      },
      kinshipDropdownOptionsDefault() {
        return this.isChildOrSpouse
          ? kinshipOptionsWithChildAndSpouse
          : kinshipOptions;
      },
      kinshipDropdownOptionsGB() {
        const kinships = this.isChildOrSpouse
          ? kinshipOptionsWithChildAndSpouse
          : kinshipOptions;
        const fianceIndex = kinships.findIndex(k => k.value == 'FIANCE');
        kinships.splice(fianceIndex, 1);

        return kinships;
      },
      showDateOfBirth() {
        return this.relation == 'CHILD';
      },
      showIsAdultCheckbox() {
        if (this.relation !== 'LIVING_WILL_DECISION_MAKER') return false;
        if (this.person.dateOfBirth && getAge(this.person.dateOfBirth) >= 18)
          return false;
        return true;
      },
      isAdultCheckboxDisabled() {
        return this.person.dateOfBirth && getAge(this.person.dateOfBirth) < 18;
      },
      isEditForm() {
        return this.personToUpdate && this.personToUpdate.id ? true : false;
      },
      isChildOrSpouse() {
        return (
          ['CHILD', 'SPOUSE'].includes(this.relation) ||
          ['CHILD', 'SPOUSE'].includes(this.person.kinship)
        );
      },
      currentDate() {
        return new Date().toJSON().slice(0, 10);
      },
      idOrPassportNumberLabel() {
        if (this.identityNumber.idType == 'PASSPORT') {
          return 'Passport number*';
        } else if (this.identityNumber.idType == 'ID') {
          return 'National ID number*';
        } else {
          return 'ID or passport number*';
        }
      },
      idOrPassportCountryLabel() {
        if (this.identityNumber.idType == 'PASSPORT') {
          return 'Nationality*';
        } else if (this.identityNumber.idType == 'ID') {
          return 'Nationality*';
        } else {
          return 'Nationality*';
        }
      },
      idTypeSelected() {
        return (
          this.identityNumber.idType == 'ID' ||
          this.identityNumber.idType == 'PASSPORT'
        );
      },
      phoneNumberSignificant() {
        const pn = parsePhoneNumber(this.person.contactNumber, {
          regionCode: this.person.phoneCountryCode,
        });
        return pn.valid ? pn.number.significant : this.person.contactNumber;
      },
      phoneNumberSignificant2() {
        const pn = parsePhoneNumber(this.person.contactNumber2, {
          regionCode: this.person.phoneCountryCode2,
        });
        return pn.valid ? pn.number.significant : this.person.contactNumber2;
      },
      phoneCountryDefault() {
        return this.person.phoneCountryCode || this.testator.willCountry;
      },
      phoneCountryDefault2() {
        return this.person.phoneCountryCode2 || this.testator.willCountry;
      },
      determineEmailRequired() {
        return this.emailRequired || this.determineDecisionMaker;
      },
      determineDecisionMaker() {
        if (
          this.person?.testatorRelationships?.find(
            relationship =>
              relationship.relation === 'LIVING_WILL_DECISION_MAKER'
          )
        ) {
          return this.person;
        }
      },
      determineEmergencyContact() {
        return this.person?.testatorRelationships?.find(
          relationship => relationship.relation === 'EMERGENCY_CONTACT'
        );
      },
    },
    countryDropdownOptions: countryDropdownOptions,
    watch: {
      personToUpdate(_value) {
        this.setPerson();
        this.setPredefinedKinship();
        if (this.updateMissingDataBeforeCreateRelation) {
          this.vueTelInputKey = 'reload';
          setTimeout(() => {
            this.$refs.observer.validate();
          }, 200);
        }
      },
      dateMenu(value) {
        value &&
          this.$nextTick(() => (this.$refs.dateMenu.activePicker = 'YEAR'));
      },
    },
    created() {
      if (this.personToUpdate && this.personToUpdate.id) {
        this.setPerson();
      }
      this.setPredefinedKinship();
    },
    methods: {
      idOrPassportValidationRules() {
        if (
          this.identityNumber.idType === 'ID' &&
          this.identityNumber.countryCode === 'ZA'
        )
          return 'required|id_number';
        if (
          this.identityNumber.idType === 'ID' &&
          this.identityNumber.countryCode !== 'ZA'
        )
          return 'required';

        if (
          this.identityNumber.idType === 'PASSPORT' &&
          this.identityNumber.countryCode === 'ZA'
        )
          return 'required';
        if (
          this.identityNumber.idType === 'PASSPORT' &&
          this.identityNumber.countryCode !== 'ZA'
        )
          return 'required';
      },
      setPerson() {
        // using native JSON functions removes reactivity
        // so we can clone an object without mutating the original source
        this.person = JSON.parse(JSON.stringify(this.personToUpdate));
        if (this.relation === 'LIVING_WILL_DECISION_MAKER') {
          this.isAdultConfirmed = !!this.person.isAdult;
        }
        if (this.person.contactNumber && this.person.phoneCountryCode) {
          this.person.contactNumber = this.phoneNumberSignificant;
        } else {
          this.person.phoneCountryCode = 'ZA';
        }
        if (this.person.contactNumber2 && this.person.phoneCountryCode2) {
          this.person.contactNumber2 = this.phoneNumberSignificant2;
        } else {
          this.person.phoneCountryCode2 = 'ZA';
        }
        if (this.person.identityNumber) {
          this.identityNumber = this.person.identityNumber;
        }
      },
      setPredefinedKinship() {
        if (this.relation == 'CHILD' || this.relation == 'SPOUSE') {
          this.person.kinship = this.relation;
        }
      },
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.createOrUpdatePerson();
          } else {
            const errors = this.$refs.observer.ctx.errors;
          }
        });
      },
      createOrUpdatePerson() {
        if (this.isEditForm) {
          this.updatePerson();
        } else {
          if (this.createNewPerson) {
            this.createPerson();
          } else {
            this.$emit('newPersonAdded', this.person);
            this.closeDialog();
          }
        }
      },
      createPerson() {
        const personInputDetails = this.personInputDetails();
        personInputDetails.testatorId = this.testatorId;
        const relation = this.relation;

        this.$apollo
          .mutate({
            mutation: CREATE_PERSON_MUTATION,
            variables: {
              relation,
              personInputDetails,
            },
            update: (cache, { data: { createPerson } }) => {
              this.updateCacheAfterCreatePerson(cache, createPerson);
            },
          })
          .then(response => {
            this.personCreated(response.data.createPerson);
            this.closeDialog();
          })
          .catch(error => {
            const personExistsError = error.message.includes(
              'This person already exists.'
            );
            if (personExistsError) {
              this.errorMessage =
                'You cannot add ' +
                this.person.firstNames +
                ' ' +
                this.person.lastName +
                ' as they already exist. Please select from the options above.';
              this.$nextTick(() =>
                document
                  .getElementById('error')
                  .scrollIntoView({ block: 'center', behavior: 'smooth' })
              );
            } else {
              this.$emit('errorThrown', this.errorMessage);
              throw error;
            }
            this.buttonLoading = false;
          });
      },
      updateCacheAfterCreatePerson(cache, createPerson) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        // add person to cache
        data.testator.people.push(createPerson);

        // update hasChildren field
        if (this.relation == 'CHILD' && data.testator.hasChildren == false) {
          data.testator.hasChildren = true;
        }

        cache.writeQuery({ ...query, data: data });
      },
      updatePerson() {
        const personInputDetails = this.personInputDetails();
        const id = this.person.id;

        if (
          personInputDetails.identityNumber === undefined ||
          personInputDetails.identityNumber === 'n/a'
        ) {
          personInputDetails.identityNumber = { destroy: true };
        }
        if (
          personInputDetails.identityNumber !== undefined &&
          personInputDetails.identityNumber !== 'n/a' &&
          this.relationsWithIDs.includes(this.relation)
        ) {
          personInputDetails.identityNumber.refresh = true;
        }

        this.$apollo
          .mutate({
            mutation: UPDATE_PERSON_MUTATION,
            variables: {
              id,
              personInputDetails,
            },
            update: (cache, { data: { updatePerson } }) => {
              this.updateCacheAfterUpdatePerson(cache, updatePerson);
            },
          })
          .then(_response => {
            if (this.updateMissingDataBeforeCreateRelation) {
              this.createTestatorRelationship();
            } else {
              this.closeDialog();
            }
          })
          .catch(error => {
            this.$emit('errorThrown');
            this.buttonLoading = false;
            throw error;
          });
      },
      updateCacheAfterUpdatePerson(cache, updatePerson) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var person = data.testator.people.find(p => p.id == updatePerson.id);
        var person = updatePerson;

        cache.writeQuery({ ...query, data: data });
      },
      createTestatorRelationship() {
        const testatorRelationshipInputDetails = {
          relation: this.relation,
          personId: this.person.id,
        };

        this.$apollo
          .mutate({
            mutation: CREATE_TESTATOR_RELATIONSHIP_MUTATION,
            variables: {
              testatorRelationshipInputDetails,
            },
            update: (cache, { data: { createTestatorRelationship } }) => {
              this.updateCacheAfterCreateTestatorRelationship(
                cache,
                createTestatorRelationship
              );
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.$emit('errorThrown');
            this.buttonLoading = false;
            throw error;
          });
      },
      updateCacheAfterCreateTestatorRelationship(
        cache,
        createTestatorRelationship
      ) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var person = data.testator.people.find(
          p => p.id == createTestatorRelationship.personId
        );
        person.testatorRelationships.push(createTestatorRelationship);

        cache.writeQuery({ ...query, data: data });
      },
      personInputDetails() {
        var personInputDetails = {
          firstNames: this.person.firstNames,
          lastName: this.person.lastName,
          dateOfBirth: this.person.dateOfBirth,
          email: this.person.email,
          kinship: this.person.kinship,
          gender: this.person.gender,
          isAdult:
            this.relation === 'LIVING_WILL_DECISION_MAKER'
              ? this.isAdultConfirmed
              : undefined,
        };
        if (this.idTypeSelected) {
          personInputDetails.identityNumber = {
            countryCode: this.identityNumber.countryCode,
            number: this.identityNumber.number,
            idType: this.identityNumber.idType,
          };
        }

        if (this.person.contactNumber) {
          const significantNumber = this.phoneNumberSignificant;
          if (significantNumber) {
            personInputDetails.contactNumber =
              this.countryDialCode + significantNumber;
          } else {
            personInputDetails.contactNumber =
              this.countryDialCode + this.person.contactNumber;
          }
          personInputDetails.phoneCountryCode =
            this.person.phoneCountryCode || 'ZA';
        } else {
          personInputDetails.contactNumber = null;
          personInputDetails.phoneCountryCode = null;
        }

        if (this.person.contactNumber2) {
          const significantNumber2 = this.phoneNumberSignificant2;
          if (significantNumber2) {
            personInputDetails.contactNumber2 =
              this.countryDialCode2 + significantNumber2;
          } else {
            personInputDetails.contactNumber2 =
              this.countryDialCode2 + this.person.contactNumber2;
          }
          personInputDetails.phoneCountryCode2 =
            this.person.phoneCountryCode2 || 'ZA';
        } else {
          personInputDetails.contactNumber2 = null;
          personInputDetails.phoneCountryCode2 = null;
        }

        return personInputDetails;
      },
      closeDialog() {
        this.$emit('closeDialog');
        this.person = {};
        this.identityNumber = {};
        this.buttonLoading = false;
      },
      countrySelected(val) {
        this.person.phoneCountryCode = val.iso2;
        this.countryDialCode = '+' + val.dialCode.toString();
      },
      countrySelected2(val) {
        this.person.phoneCountryCode2 = val.iso2;
        this.countryDialCode2 = '+' + val.dialCode.toString();
      },
      determineDeleteCheck() {
        if (
          this.$route.name !== 'LivingWill' &&
          this.$route.name !== 'ChildrensGuardian' &&
          this.determineDecisionMaker
        ) {
          this.informTestatorLivingWillRelation();
        } else {
          this.$emit('deleteCheck');
        }
      },
      informTestatorLivingWillRelation() {
        this.$emit('decisionMakerDialogShow', this.determineDecisionMaker);
      },
      toCapitalCase(str) {
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
    },
  };
</script>

<style lang="scss">
  .person-dialog-form-container {
    .v-radio {
      margin-right: 8px !important;
    }

    .button-container {
      margin-bottom: 14px;
      margin-top: 40px;
      display: flex;
      gap: 20px;
    }

    .crud-card-actions {
      width: 100%;
    }

    input[name='telephone'] {
      display: none;
    }

    .vti__dropdown-list {
      padding-left: 0;
      max-width: 370px;
      top: auto !important;
      bottom: -moz-calc(100% + 5px) !important;
      bottom: -webkit-calc(100% + 5px) !important;
      bottom: -o-calc(100% + 5px) !important;
      bottom: calc(100% + 5px) !important;

      .vti__dropdown {
        color: rgba(0, 0, 0, 0.87) !important;
      }
    }
    #date_of_birth {
      .v-input {
        padding-top: 12px;
        margin-top: 24px;
      }
    }
  }
</style>
